/**
 * Cooling System 2 — Graph generators per plant
 * 
 * Each plant (centrale) has its own network topology.
 * This file generates the React Flow nodes & edges for each plant.
 */
import type { Edge, Node } from '@xyflow/react';
import type { IBaseData } from '@shared/production-editor';
import type { CondenserCoolingType } from './types';

const EDGE_GREY_DASHED = {
    stroke: 'var(--nj-core-color-grey-400)',
    strokeWidth: 1,
    strokeDasharray: '4 4',
};

export type PlantId = 'cber' | 'cauber' | 'cetoile' | 'cprintemps' | 'ccanada' | 'ctokyo' | 'cbnf';

export interface GroupConfigs {
    [groupId: string]: CondenserCoolingType;
}

export function buildGraphForPlant(
    plantId: PlantId,
    groupConfigs?: GroupConfigs
): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    switch (plantId) {
        case 'cber':
            return buildCBER(groupConfigs);
        case 'cauber':
            return buildCAuber();
        case 'cetoile':
            return buildCEtoile();
        case 'cprintemps':
            return buildCPrintemps();
        case 'ccanada':
            return buildCCanada();
        case 'ctokyo':
            return buildCTokyo();
        case 'cbnf':
            return buildCBNF();
        default:
            return buildCBER(groupConfigs); // Fallback
    }
}

// ── CBER (3 lignes séparées, 4 pompes) ─────────────────────────────────

function buildCBER(groupConfigs?: GroupConfigs): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    const nodes: Node<IBaseData>[] = [
        {
            id: 'power-bercy',
            type: 'contract',
            position: { x: 50, y: 20 },
            data: {
                entityName: 'PowerContract',
                entityLabel: "Contrat d'électricité",
                label: 'Contrat élec BERCY',
            },
            width: 165,
            height: 130,
        },
        {
            id: 'cooling-bercy',
            type: 'group',
            position: { x: 50, y: 190 },
            data: {
                entityName: 'WaterCooling',
                entityLabel: 'Système de refroidissement à air',
                label: 'Système de refroidissement à air BERCY',
                coolingSystemType: 'air',
                // Valeurs par défaut du formulaire
                linkedProductionLines: ['group-line1', 'group-line2', 'group-line3'],
                condenserInletTempMin: 288.15,
                condenserOutletTempMax: 303.15,
                electricityContract: '',
                waterContract: 'TAR_Water_Contract_Opera',
                linkedCoolingTowers: ['tar-bercy-1', 'tar-bercy-2'],
                condenserInletAlpha: -9.3925,
                condenserInletBeta: 1528.6,
                condenserInletGamma: 0.2161,
                condenserInletEta: 0.0169,
                waterFlowAlpha: 4.0272,
                waterFlowBeta: 0,
                softenerEfficiency: 0.05,
                tarStaticHeight: 0,
                tarPressureLoss: 0,
                tarNominalFlow: 0,
            },
            width: 320,
            height: 150,
            style: {
                backgroundColor: 'white',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: '8px',
            },
        },
        {
            id: 'tar-bercy-1',
            type: 'asset',
            position: { x: 70, y: 220 },
            data: {
                entityName: 'CoolingTower',
                entityLabel: 'TAR',
                label: 'TAR 1',
                nominalElectricalPower: 65000,
                nominalThermalPower: 4000000,
                numberOfFans: 2,
                electricPowerAlpha: -1,
                electricPowerBeta: 3.0109,
                fansElectricPowerGamma: 0.5551,
                fanElectricPowerCoeffM: 1.3110,
                airCoolingSystem: 'refroidissement-tar-bercy',
            },
            width: 130,
            height: 90,
            parentId: 'cooling-bercy',
            extent: 'parent' as const,
        },
        {
            id: 'tar-bercy-2',
            type: 'asset',
            position: { x: 210, y: 220 },
            data: {
                entityName: 'CoolingTower',
                entityLabel: 'TAR',
                label: 'TAR 2',
                nominalElectricalPower: 65000,
                nominalThermalPower: 4000000,
                numberOfFans: 2,
                electricPowerAlpha: -1,
                electricPowerBeta: 3.0109,
                fansElectricPowerGamma: 0.5551,
                fanElectricPowerCoeffM: 1.3110,
                airCoolingSystem: 'refroidissement-tar-bercy',
            },
            width: 130,
            height: 90,
            parentId: 'cooling-bercy',
            extent: 'parent' as const,
        },
        {
            id: 'return-bercy',
            type: 'asset',
            position: { x: 50, y: 380 },
            data: {
                entityName: 'Return',
                entityLabel: 'Retour',
                label: 'BERCY Retour',
            },
            width: 140,
            height: 120,
        },
        // Ligne 1
        {
            id: 'group-line1',
            type: 'group',
            position: { x: 300, y: 50 },
            data: { 
                entityName: 'Group', 
                entityLabel: 'Ligne de production', 
                label: 'Ligne 1 BERCY',
                condenserCoolingType: groupConfigs?.['group-line1'] || 'parallel',
            },
            width: 400,
            height: 180,
            style: {
                backgroundColor: 'white',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: '8px',
            },
        },
        {
            id: 'chiller-gf1-a',
            type: 'asset',
            position: { x: 320, y: 80 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF1-A', chillerType: 'Centrifuge' },
            width: 160,
            height: 130,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        {
            id: 'chiller-gf1-b',
            type: 'asset',
            position: { x: 510, y: 80 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF1-B', chillerType: 'Centrifuge' },
            width: 160,
            height: 130,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        // Ligne 2
        {
            id: 'group-line2',
            type: 'group',
            position: { x: 300, y: 260 },
            data: { 
                entityName: 'Group', 
                entityLabel: 'Ligne de production', 
                label: 'Ligne 2 BERCY',
                condenserCoolingType: groupConfigs?.['group-line2'] || 'parallel',
            },
            width: 400,
            height: 180,
            style: {
                backgroundColor: 'white',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: '8px',
            },
        },
        {
            id: 'chiller-gf2-a',
            type: 'asset',
            position: { x: 320, y: 290 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF2-A', chillerType: 'Centrifuge' },
            width: 160,
            height: 130,
            parentId: 'group-line2',
            extent: 'parent' as const,
        },
        {
            id: 'chiller-gf2-b',
            type: 'asset',
            position: { x: 510, y: 290 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF2-B', chillerType: 'Centrifuge' },
            width: 160,
            height: 130,
            parentId: 'group-line2',
            extent: 'parent' as const,
        },
        // Ligne 3
        {
            id: 'group-line3',
            type: 'group',
            position: { x: 300, y: 470 },
            data: { 
                entityName: 'Group', 
                entityLabel: 'Ligne de production', 
                label: 'Ligne 3 BERCY',
                condenserCoolingType: groupConfigs?.['group-line3'] || 'parallel',
            },
            width: 400,
            height: 180,
            style: {
                backgroundColor: 'white',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: '8px',
            },
        },
        {
            id: 'chiller-gf3-a',
            type: 'asset',
            position: { x: 320, y: 500 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF3-A', chillerType: 'Centrifuge' },
            width: 160,
            height: 130,
            parentId: 'group-line3',
            extent: 'parent' as const,
        },
        {
            id: 'chiller-gf3-b',
            type: 'asset',
            position: { x: 510, y: 500 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF3-B', chillerType: 'Centrifuge' },
            width: 160,
            height: 130,
            parentId: 'group-line3',
            extent: 'parent' as const,
        },
        // Groupe pompes
        {
            id: 'group-pumps',
            type: 'group',
            position: { x: 750, y: 50 },
            data: { entityName: 'Group', entityLabel: 'Groupe de pompes', label: 'CBER_pumps_group' },
            width: 240,
            height: 600,
            style: {
                backgroundColor: 'white',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: '8px',
            },
        },
        {
            id: 'pump-1',
            type: 'asset',
            position: { x: 770, y: 80 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 1 BERCY' },
            width: 180,
            height: 120,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-2',
            type: 'asset',
            position: { x: 770, y: 220 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 2 BERCY' },
            width: 180,
            height: 120,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-3',
            type: 'asset',
            position: { x: 770, y: 360 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 3 BERCY' },
            width: 180,
            height: 120,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-4',
            type: 'asset',
            position: { x: 770, y: 500 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 4 BERCY' },
            width: 180,
            height: 120,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'departure-bercy',
            type: 'asset',
            position: { x: 1050, y: 300 },
            data: { entityName: 'Departure', entityLabel: 'Départ', label: 'BERCY Départ' },
            width: 140,
            height: 120,
        },
    ];

    const edges: Edge[] = [
        // Le système de refroidissement rejoint exactement le même point gauche que le retour.
        { id: 'e-cooling-line1', source: 'cooling-bercy', target: 'group-line1', type: 'animatedFlowGreen', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e-cooling-line2', source: 'cooling-bercy', target: 'group-line2', type: 'animatedFlowGreen', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e-cooling-line3', source: 'cooling-bercy', target: 'group-line3', type: 'animatedFlowGreen', sourceHandle: 's-right', targetHandle: 'left' },
        // Connexions du retour vers les lignes
        { id: 'e-return-line1', source: 'return-bercy', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line2', source: 'return-bercy', target: 'group-line2', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line3', source: 'return-bercy', target: 'group-line3', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        // Connexions des lignes vers les pompes
        { id: 'e-line1-pumps', source: 'group-line1', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e-line2-pumps', source: 'group-line2', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e-line3-pumps', source: 'group-line3', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        // Connexion pompes vers départ
        { id: 'e-pumps-departure', source: 'group-pumps', target: 'departure-bercy', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
    ];

    // Add series condenser edges if configured
    if (groupConfigs?.['group-line1'] === 'series') {
        edges.push({ 
            id: 'e-gf1-series', 
            source: 'chiller-gf1-a', 
            target: 'chiller-gf1-b', 
            type: 'animatedFlow',
            sourceHandle: 'right',
            targetHandle: 'left',
        });
    }
    if (groupConfigs?.['group-line2'] === 'series') {
        edges.push({ 
            id: 'e-gf2-series', 
            source: 'chiller-gf2-a', 
            target: 'chiller-gf2-b', 
            type: 'animatedFlow',
            sourceHandle: 'right',
            targetHandle: 'left',
        });
    }
    if (groupConfigs?.['group-line3'] === 'series') {
        edges.push({ 
            id: 'e-gf3-series', 
            source: 'chiller-gf3-a', 
            target: 'chiller-gf3-b', 
            type: 'animatedFlow',
            sourceHandle: 'right',
            targetHandle: 'left',
        });
    }

    return { nodes, edges };
}

// ── CAuber (2 lignes, 2 pompes) ────────────────────────────────────────

function buildCAuber(): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    const nodes: Node<IBaseData>[] = [
        {
            id: 'power-auber',
            type: 'contract',
            position: { x: 50, y: 20 },
            data: { entityName: 'PowerContract', entityLabel: "Contrat d'électricité", label: 'Contrat élec Auber' },
            width: 165,
            height: 130,
        },
        {
            id: 'water-auber',
            type: 'contract',
            position: { x: 250, y: 20 },
            data: { entityName: 'WaterContract', entityLabel: "Contrat d'eau", label: 'TAF_Water_Contrat_Auber' },
            width: 165,
            height: 130,
        },
        {
            id: 'cooling-auber',
            type: 'asset',
            position: { x: 50, y: 190 },
            data: { entityName: 'WaterCooling', entityLabel: 'Refroidissement', label: 'Refroidissement par TAF CAuber' },
            width: 180,
            height: 100,
        },
        {
            id: 'tower-auber',
            type: 'asset',
            position: { x: 50, y: 310 },
            data: { entityName: 'CoolingTower', entityLabel: 'Tour', label: 'Tour aéroréfrigérante' },
            width: 180,
            height: 100,
        },
        {
            id: 'return-auber',
            type: 'asset',
            position: { x: 50, y: 450 },
            data: { entityName: 'Return', entityLabel: 'Retour', label: 'AUBER Retour' },
            width: 140,
            height: 120,
        },
        // Ligne 1
        {
            id: 'group-line1',
            type: 'group',
            position: { x: 300, y: 180 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 1 AUBER' },
            width: 240,
            height: 160,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf1a',
            type: 'asset',
            position: { x: 320, y: 200 },
            data: { entityName: 'Chiller', entityLabel: 'Multi-centrifuge', label: 'GF1A AUBER', chillerType: 'Multi-centrifuge' },
            width: 160,
            height: 120,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        // Ligne 2
        {
            id: 'group-line2',
            type: 'group',
            position: { x: 300, y: 370 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 2 AUBER' },
            width: 240,
            height: 160,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf2a',
            type: 'asset',
            position: { x: 320, y: 390 },
            data: { entityName: 'Chiller', entityLabel: 'Multi-centrifuge', label: 'GF2A AUBER', chillerType: 'Multi-centrifuge' },
            width: 160,
            height: 120,
            parentId: 'group-line2',
            extent: 'parent' as const,
        },
        // Groupe pompes
        {
            id: 'group-pumps',
            type: 'group',
            position: { x: 600, y: 230 },
            data: { entityName: 'Group', entityLabel: 'Pompes', label: 'CAuber_pumps_group' },
            width: 220,
            height: 250,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'pump-1',
            type: 'asset',
            position: { x: 620, y: 250 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 1 AUBER' },
            width: 160,
            height: 100,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-2',
            type: 'asset',
            position: { x: 620, y: 360 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 2 AUBER' },
            width: 160,
            height: 100,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'departure-auber',
            type: 'asset',
            position: { x: 870, y: 300 },
            data: { entityName: 'Departure', entityLabel: 'Départ', label: 'AUBER Départ' },
            width: 140,
            height: 120,
        },
    ];

    const edges: Edge[] = [
        { id: 'e1', source: 'power-auber', target: 'cooling-auber', type: 'default', sourceHandle: 'bottom', style: EDGE_GREY_DASHED },
        { id: 'e2', source: 'cooling-auber', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line1', source: 'return-auber', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line2', source: 'return-auber', target: 'group-line2', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e3', source: 'group-line1', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e4', source: 'group-line2', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e5', source: 'group-pumps', target: 'departure-auber', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
    ];

    return { nodes, edges };
}

// ── CEtoile (2 lignes, 2 pompes) ───────────────────────────────────────

function buildCEtoile(): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    const nodes: Node<IBaseData>[] = [
        {
            id: 'power-etoile',
            type: 'contract',
            position: { x: 50, y: 20 },
            data: { entityName: 'PowerContract', entityLabel: "Contrat élec", label: 'Contrat élec Etoile' },
            width: 165,
            height: 130,
        },
        {
            id: 'water-etoile',
            type: 'contract',
            position: { x: 250, y: 20 },
            data: { entityName: 'WaterContract', entityLabel: "Contrat eau", label: 'TAF_Water_Contrat_Etoile' },
            width: 165,
            height: 130,
        },
        {
            id: 'cooling-etoile',
            type: 'asset',
            position: { x: 50, y: 190 },
            data: { entityName: 'WaterCooling', entityLabel: 'Refroidissement', label: 'Refroidissement par TAF CEtoile' },
            width: 180,
            height: 100,
        },
        {
            id: 'tower-etoile',
            type: 'asset',
            position: { x: 50, y: 310 },
            data: { entityName: 'CoolingTower', entityLabel: 'Tour', label: 'Tour aéroréfrigérante' },
            width: 180,
            height: 100,
        },
        {
            id: 'return-etoile',
            type: 'asset',
            position: { x: 50, y: 450 },
            data: { entityName: 'Return', entityLabel: 'Retour', label: 'ETOILE Retour' },
            width: 140,
            height: 120,
        },
        // Ligne 1
        {
            id: 'group-line1',
            type: 'group',
            position: { x: 300, y: 180 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 1 Étoile' },
            width: 240,
            height: 160,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf1a',
            type: 'asset',
            position: { x: 320, y: 200 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF1AETOILE', chillerType: 'Centrifuge' },
            width: 160,
            height: 120,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        // Ligne 2
        {
            id: 'group-line2',
            type: 'group',
            position: { x: 300, y: 370 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 2 Étoile' },
            width: 240,
            height: 160,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf2a',
            type: 'asset',
            position: { x: 320, y: 390 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF2AETOILE', chillerType: 'Centrifuge' },
            width: 160,
            height: 120,
            parentId: 'group-line2',
            extent: 'parent' as const,
        },
        // Groupe pompes
        {
            id: 'group-pumps',
            type: 'group',
            position: { x: 600, y: 230 },
            data: { entityName: 'Group', entityLabel: 'Pompes', label: 'CEtoile_pumps_group' },
            width: 220,
            height: 250,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'pump-1',
            type: 'asset',
            position: { x: 620, y: 250 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 1 CEtoile' },
            width: 160,
            height: 100,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-2',
            type: 'asset',
            position: { x: 620, y: 360 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 2 CEtoile' },
            width: 160,
            height: 100,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'departure-etoile',
            type: 'asset',
            position: { x: 870, y: 300 },
            data: { entityName: 'Departure', entityLabel: 'Départ', label: 'ETOILE Départ' },
            width: 140,
            height: 120,
        },
    ];

    const edges: Edge[] = [
        { id: 'e1', source: 'power-etoile', target: 'cooling-etoile', type: 'default', sourceHandle: 'bottom', style: EDGE_GREY_DASHED },
        { id: 'e2', source: 'cooling-etoile', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line1', source: 'return-etoile', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line2', source: 'return-etoile', target: 'group-line2', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e3', source: 'group-line1', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e4', source: 'group-line2', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e5', source: 'group-pumps', target: 'departure-etoile', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
    ];

    return { nodes, edges };
}

// ── CPrintemps (4 lignes verticales, 1 pompe) ──────────────────────────

function buildCPrintemps(): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    const nodes: Node<IBaseData>[] = [
        {
            id: 'power-printemps',
            type: 'contract',
            position: { x: 50, y: 20 },
            data: { entityName: 'PowerContract', entityLabel: "Contrat élec", label: 'Contrat élec Printemps' },
            width: 165,
            height: 130,
        },
        {
            id: 'water-printemps',
            type: 'contract',
            position: { x: 250, y: 20 },
            data: { entityName: 'WaterContract', entityLabel: "Contrat eau", label: 'TAF_Water_Contrat_Printemps' },
            width: 165,
            height: 130,
        },
        {
            id: 'cooling-printemps',
            type: 'asset',
            position: { x: 50, y: 190 },
            data: { entityName: 'WaterCooling', entityLabel: 'Refroidissement', label: 'Refroidissement par TAF Printemps' },
            width: 180,
            height: 100,
        },
        {
            id: 'tower-printemps',
            type: 'asset',
            position: { x: 50, y: 310 },
            data: { entityName: 'CoolingTower', entityLabel: 'Tour', label: 'Tour aéroréfrigérante' },
            width: 180,
            height: 100,
        },
        {
            id: 'return-printemps',
            type: 'asset',
            position: { x: 50, y: 450 },
            data: { entityName: 'Return', entityLabel: 'Retour', label: 'PRINTEMPS Retour' },
            width: 140,
            height: 120,
        },
        // 4 lignes verticales
        {
            id: 'group-line1',
            type: 'group',
            position: { x: 300, y: 50 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 1 PRINTEMPS' },
            width: 200,
            height: 140,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf1',
            type: 'asset',
            position: { x: 320, y: 70 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF1 PRINTEMPS', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        {
            id: 'group-line2',
            type: 'group',
            position: { x: 300, y: 210 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 2 PRINTEMPS' },
            width: 200,
            height: 140,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf2',
            type: 'asset',
            position: { x: 320, y: 230 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF2 PRINTEMPS', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line2',
            extent: 'parent' as const,
        },
        {
            id: 'group-line3',
            type: 'group',
            position: { x: 300, y: 370 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 3 PRINTEMPS' },
            width: 200,
            height: 140,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf3',
            type: 'asset',
            position: { x: 320, y: 390 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF3 PRINTEMPS', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line3',
            extent: 'parent' as const,
        },
        {
            id: 'group-line4',
            type: 'group',
            position: { x: 300, y: 530 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 4 PRINTEMPS' },
            width: 200,
            height: 140,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf4',
            type: 'asset',
            position: { x: 320, y: 550 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF4 PRINTEMPS', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line4',
            extent: 'parent' as const,
        },
        // 1 pompe
        {
            id: 'group-pumps',
            type: 'group',
            position: { x: 560, y: 280 },
            data: { entityName: 'Group', entityLabel: 'Pompes', label: 'Pompe_pumps_group PRINTEMPS' },
            width: 180,
            height: 140,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'pump-1',
            type: 'asset',
            position: { x: 580, y: 300 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe_ATP01' },
            width: 120,
            height: 100,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'departure-printemps',
            type: 'asset',
            position: { x: 790, y: 300 },
            data: { entityName: 'Departure', entityLabel: 'Départ', label: 'PRINTEMPS Départ' },
            width: 140,
            height: 120,
        },
    ];

    const edges: Edge[] = [
        { id: 'e1', source: 'power-printemps', target: 'cooling-printemps', type: 'default', sourceHandle: 'bottom', style: EDGE_GREY_DASHED },
        { id: 'e2', source: 'cooling-printemps', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line1', source: 'return-printemps', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line2', source: 'return-printemps', target: 'group-line2', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line3', source: 'return-printemps', target: 'group-line3', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line4', source: 'return-printemps', target: 'group-line4', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e3', source: 'group-line1', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e4', source: 'group-line2', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e5', source: 'group-line3', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e6', source: 'group-line4', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e7', source: 'group-pumps', target: 'departure-printemps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
    ];

    return { nodes, edges };
}

// ── CCanada (1 ligne verticale géante, 7 chillers, 4 pompes) ───────────

function buildCCanada(): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    const nodes: Node<IBaseData>[] = [
        {
            id: 'power-canada',
            type: 'contract',
            position: { x: 50, y: 20 },
            data: { entityName: 'PowerContract', entityLabel: "Contrat élec", label: 'Contrat élec Canada' },
            width: 165,
            height: 130,
        },
        {
            id: 'water-canada',
            type: 'contract',
            position: { x: 250, y: 20 },
            data: { entityName: 'WaterContract', entityLabel: "Contrat eau", label: 'Water_Cold_Contrat_Canada' },
            width: 165,
            height: 130,
        },
        {
            id: 'cooling-canada',
            type: 'asset',
            position: { x: 50, y: 190 },
            data: { entityName: 'WaterCooling', entityLabel: 'Refroidissement', label: 'Refroidissement à CAnada' },
            width: 180,
            height: 100,
        },
        {
            id: 'tower-canada',
            type: 'asset',
            position: { x: 50, y: 310 },
            data: { entityName: 'WaterCooling', entityLabel: 'Système', label: 'Système refroidissement eau' },
            width: 180,
            height: 100,
        },
        {
            id: 'return-canada',
            type: 'asset',
            position: { x: 50, y: 450 },
            data: { entityName: 'Return', entityLabel: 'Retour', label: 'CANADA Retour' },
            width: 140,
            height: 120,
        },
        // 1 ligne géante avec 7 chillers
        {
            id: 'group-line1',
            type: 'group',
            position: { x: 300, y: 50 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 1 CAnada' },
            width: 200,
            height: 900,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf1',
            type: 'asset',
            position: { x: 320, y: 70 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF1 CAnada', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        {
            id: 'chiller-gf2',
            type: 'asset',
            position: { x: 320, y: 190 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF2 CAnada', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        {
            id: 'chiller-gf3',
            type: 'asset',
            position: { x: 320, y: 310 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF3 CAnada', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        {
            id: 'chiller-gf4',
            type: 'asset',
            position: { x: 320, y: 430 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF4 CAnada', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        {
            id: 'chiller-gf5',
            type: 'asset',
            position: { x: 320, y: 550 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF5 CAnada', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        {
            id: 'chiller-gf6',
            type: 'asset',
            position: { x: 320, y: 670 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF6 CAnada', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        {
            id: 'chiller-gf7',
            type: 'asset',
            position: { x: 320, y: 790 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF7 CAnada', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        // Groupe pompes (4 pompes empilées)
        {
            id: 'group-pumps',
            type: 'group',
            position: { x: 560, y: 150 },
            data: { entityName: 'Group', entityLabel: 'Pompes', label: 'CCanada_pumps_group' },
            width: 180,
            height: 620,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'pump-1',
            type: 'asset',
            position: { x: 580, y: 170 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 1 CAnada-GF6203' },
            width: 120,
            height: 120,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-2',
            type: 'asset',
            position: { x: 580, y: 310 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 2 CAnada-GF6204' },
            width: 120,
            height: 120,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-3',
            type: 'asset',
            position: { x: 580, y: 450 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 3 CAnada-GF6202' },
            width: 120,
            height: 120,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-4',
            type: 'asset',
            position: { x: 580, y: 590 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 4 CAnada-GF6201' },
            width: 120,
            height: 120,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'departure-canada',
            type: 'asset',
            position: { x: 790, y: 400 },
            data: { entityName: 'Departure', entityLabel: 'Départ', label: 'CANADA Départ' },
            width: 140,
            height: 120,
        },
    ];

    const edges: Edge[] = [
        { id: 'e1', source: 'power-canada', target: 'cooling-canada', type: 'default', sourceHandle: 'bottom', style: EDGE_GREY_DASHED },
        { id: 'e2', source: 'cooling-canada', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line1', source: 'return-canada', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e3', source: 'group-line1', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e4', source: 'group-pumps', target: 'departure-canada', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
    ];

    return { nodes, edges };
}

// ── CTokyo (5 lignes, 6 chillers, 4 pompes) ────────────────────────────

function buildCTokyo(): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    const nodes: Node<IBaseData>[] = [
        {
            id: 'power-tokyo',
            type: 'contract',
            position: { x: 50, y: 20 },
            data: { entityName: 'PowerContract', entityLabel: "Contrat élec", label: 'Contrat élec Tokyo' },
            width: 165,
            height: 130,
        },
        {
            id: 'water-tokyo',
            type: 'contract',
            position: { x: 250, y: 20 },
            data: { entityName: 'WaterContract', entityLabel: "Contrat eau", label: 'TAF_Water_Contrat_TOKYO' },
            width: 165,
            height: 130,
        },
        {
            id: 'cooling-tokyo',
            type: 'asset',
            position: { x: 50, y: 190 },
            data: { entityName: 'WaterCooling', entityLabel: 'Refroidissement', label: 'Refroidissement TAF CTOYO' },
            width: 180,
            height: 100,
        },
        {
            id: 'tower-tokyo',
            type: 'asset',
            position: { x: 50, y: 310 },
            data: { entityName: 'CoolingTower', entityLabel: 'Evapo', label: 'Evapo' },
            width: 180,
            height: 100,
        },
        {
            id: 'return-tokyo',
            type: 'asset',
            position: { x: 50, y: 450 },
            data: { entityName: 'Return', entityLabel: 'Retour', label: 'TOKYO Retour' },
            width: 140,
            height: 120,
        },
        // 5 lignes
        {
            id: 'group-line1',
            type: 'group',
            position: { x: 300, y: 50 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 1 TOKYO' },
            width: 200,
            height: 140,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf1a',
            type: 'asset',
            position: { x: 320, y: 70 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF1A TOKYO', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        {
            id: 'group-line2',
            type: 'group',
            position: { x: 300, y: 210 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 2 TOKYO' },
            width: 200,
            height: 140,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf2',
            type: 'asset',
            position: { x: 320, y: 230 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF2 TOKYO', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line2',
            extent: 'parent' as const,
        },
        {
            id: 'group-line3',
            type: 'group',
            position: { x: 300, y: 370 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 3 TOKYO' },
            width: 200,
            height: 260,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf3',
            type: 'asset',
            position: { x: 320, y: 390 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF3 TOKYO', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line3',
            extent: 'parent' as const,
        },
        {
            id: 'chiller-gf3b',
            type: 'asset',
            position: { x: 320, y: 510 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF3B TOKYO', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line3',
            extent: 'parent' as const,
        },
        {
            id: 'group-line4',
            type: 'group',
            position: { x: 300, y: 650 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 4 TOKYO' },
            width: 200,
            height: 140,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf4',
            type: 'asset',
            position: { x: 320, y: 670 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF4 TOKYO', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line4',
            extent: 'parent' as const,
        },
        {
            id: 'group-line5',
            type: 'group',
            position: { x: 300, y: 810 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 5 TOKYO' },
            width: 200,
            height: 140,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf5',
            type: 'asset',
            position: { x: 320, y: 830 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF5 TOKYO', chillerType: 'Centrifuge' },
            width: 140,
            height: 100,
            parentId: 'group-line5',
            extent: 'parent' as const,
        },
        // Groupe pompes (4 pompes)
        {
            id: 'group-pumps',
            type: 'group',
            position: { x: 560, y: 250 },
            data: { entityName: 'Group', entityLabel: 'Pompes', label: 'CTokyio_pumps_group' },
            width: 180,
            height: 540,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'pump-1',
            type: 'asset',
            position: { x: 580, y: 270 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 1 TOKYO' },
            width: 120,
            height: 100,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-2',
            type: 'asset',
            position: { x: 580, y: 390 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 2 TOKYO' },
            width: 120,
            height: 100,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-3',
            type: 'asset',
            position: { x: 580, y: 510 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 3 TOKYO' },
            width: 120,
            height: 100,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-4',
            type: 'asset',
            position: { x: 580, y: 630 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 4 TOKYO' },
            width: 120,
            height: 100,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'departure-tokyo',
            type: 'asset',
            position: { x: 790, y: 480 },
            data: { entityName: 'Departure', entityLabel: 'Départ', label: 'TOKYO Départ' },
            width: 140,
            height: 120,
        },
    ];

    const edges: Edge[] = [
        { id: 'e1', source: 'power-tokyo', target: 'cooling-tokyo', type: 'default', sourceHandle: 'bottom', style: EDGE_GREY_DASHED },
        { id: 'e2', source: 'cooling-tokyo', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line1', source: 'return-tokyo', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line2', source: 'return-tokyo', target: 'group-line2', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line3', source: 'return-tokyo', target: 'group-line3', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line4', source: 'return-tokyo', target: 'group-line4', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line5', source: 'return-tokyo', target: 'group-line5', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e3', source: 'group-line1', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e4', source: 'group-line2', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e5', source: 'group-line3', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e6', source: 'group-line4', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e7', source: 'group-line5', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e8', source: 'group-pumps', target: 'departure-tokyo', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
    ];

    return { nodes, edges };
}

// ── CBNF (1 ligne, 2 chillers, 2 pompes) ───────────────────────────────

function buildCBNF(): { nodes: Node<IBaseData>[]; edges: Edge[] } {
    const nodes: Node<IBaseData>[] = [
        {
            id: 'power-bnf',
            type: 'contract',
            position: { x: 50, y: 20 },
            data: { entityName: 'PowerContract', entityLabel: "Contrat élec", label: 'Contrat élec BNF' },
            width: 165,
            height: 130,
        },
        {
            id: 'cooling-bnf',
            type: 'asset',
            position: { x: 50, y: 190 },
            data: { entityName: 'WaterCooling', entityLabel: 'Refroidissement', label: 'Refroidissement par TAF BNF' },
            width: 180,
            height: 100,
        },
        {
            id: 'tower-bnf',
            type: 'asset',
            position: { x: 50, y: 310 },
            data: { entityName: 'CoolingTower', entityLabel: 'Tour', label: 'Tour aéroréfrigérante' },
            width: 180,
            height: 100,
        },
        {
            id: 'return-bnf',
            type: 'asset',
            position: { x: 50, y: 450 },
            data: { entityName: 'Return', entityLabel: 'Retour', label: 'BNF Retour' },
            width: 140,
            height: 120,
        },
        // 1 ligne avec 2 chillers
        {
            id: 'group-line1',
            type: 'group',
            position: { x: 300, y: 200 },
            data: { entityName: 'Group', entityLabel: 'Ligne', label: 'Ligne 1 BNF' },
            width: 240,
            height: 290,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'chiller-gf1',
            type: 'asset',
            position: { x: 320, y: 220 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF1 BNF', chillerType: 'Centrifuge' },
            width: 160,
            height: 110,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        {
            id: 'chiller-gf2',
            type: 'asset',
            position: { x: 320, y: 350 },
            data: { entityName: 'Chiller', entityLabel: 'Centrifuge', label: 'GF2 BNF', chillerType: 'Centrifuge' },
            width: 160,
            height: 110,
            parentId: 'group-line1',
            extent: 'parent' as const,
        },
        // Groupe pompes (2 pompes)
        {
            id: 'group-pumps',
            type: 'group',
            position: { x: 600, y: 250 },
            data: { entityName: 'Group', entityLabel: 'Pompes', label: 'CBNF_pumps_group' },
            width: 200,
            height: 230,
            style: { backgroundColor: 'white', border: '1px solid var(--nj-core-color-grey-300)', borderRadius: '8px' },
        },
        {
            id: 'pump-1',
            type: 'asset',
            position: { x: 620, y: 270 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 1 BNF' },
            width: 140,
            height: 90,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'pump-2',
            type: 'asset',
            position: { x: 620, y: 370 },
            data: { entityName: 'VariableSpeedPump', entityLabel: 'Pompe', label: 'Pompe distribution 2 BNF' },
            width: 140,
            height: 90,
            parentId: 'group-pumps',
            extent: 'parent' as const,
        },
        {
            id: 'departure-bnf',
            type: 'asset',
            position: { x: 850, y: 320 },
            data: { entityName: 'Departure', entityLabel: 'Départ', label: 'BNF Départ' },
            width: 140,
            height: 120,
        },
    ];

    const edges: Edge[] = [
        { id: 'e1', source: 'power-bnf', target: 'cooling-bnf', type: 'default', sourceHandle: 'bottom', style: EDGE_GREY_DASHED },
        { id: 'e2', source: 'cooling-bnf', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e-return-line1', source: 'return-bnf', target: 'group-line1', type: 'animatedFlowCyan', sourceHandle: 'right', targetHandle: 'left' },
        { id: 'e3', source: 'group-line1', target: 'group-pumps', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
        { id: 'e4', source: 'group-pumps', target: 'departure-bnf', type: 'animatedFlow', sourceHandle: 's-right', targetHandle: 'left' },
    ];

    return { nodes, edges };
}
